import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import SharedIntlProviderHOC from '../../shared-components/hocs/sharedIntlProviderHOC';

/* This Must align with the web-templates copy. If one changes, please update the other */
const termsDefinitions = defineMessages({
  peerspace_service_agreement: {
    id: 'shared_components.disclaimer.peerspace_service_agreement',
    defaultMessage: 'Peerspace\'s Services Agreement'
  },
  service_agreement: {
    id: 'shared_components.disclaimer.service_agreement',
    defaultMessage: 'Services Agreement'
  },
  privacy_policy: {
    id: 'shared_components.disclaimer.privacy_policy',
    defaultMessage: 'Privacy Policy'
  },
  cancellation_policy: {
    id: 'shared_components.disclaimer.cancellation_policy',
    defaultMessage: 'Cancellation and Refund Policy'
  },
  fees_overview: {
    id: 'shared_components.disclaimer.fees_overview',
    defaultMessage: 'Fees Overview'
  },
  community_guidelines: {
    id: 'shared_components.disclaimer.community_guidelines',
    defaultMessage: 'Community Guidelines'
  },
  stripe_service_agreement: {
    id: 'shared_components.disclaimer.stripe_service_agreement',
    defaultMessage: 'Stripe\'s Services Agreement'
  },
  stripe_connected: {
    id: 'listings.disclaimer.stripe_connected',
    defaultMessage: 'Stripe Connected Account Agreement'
  },
  instant_book: {
    id: 'shared_components.disclaimer.instant_book',
    defaultMessage: 'Peerspace’s Instant Book Guidelines'
  },
  social_event_policy: {
    id: 'shared_components.disclaimer.social_event_policy',
    defaultMessage: 'Social Event Policy'
  }
});

const prefix = SharedIntlProviderHOC.getUrlPrefix();
const zenDeskLocale = SharedIntlProviderHOC.getZendeskLocale();
const urlPrefix = prefix ? `/${prefix}` : '';

const termsLinks = {
  signup: (
    <FormattedMessage
      id="shared_components.disclaimer.signup_terms"
      defaultMessage="you agree to {terms_link} and {privacy_link}."
      values={{
        terms_link: (
          <a href={`${urlPrefix}/legal/terms`}><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>
        ),
        privacy_link: (
          <a href={`${urlPrefix}/legal/privacy`}><FormattedMessage {...termsDefinitions.privacy_policy} /></a>
        )
      }}
    />
  ),
  transactable: (
    <FormattedMessage
      id="shared_components.disclaimer.transactable_terms"
      defaultMessage="you accept and agree to Peerspace's {terms_link}, {privacy_link}, {cancellation_policy}, and {fees_overview}, and agree to abide by the {community_guidelines}."
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`}><FormattedMessage {...termsDefinitions.service_agreement} /></a>,
        privacy_link: <a href={`${urlPrefix}/legal/privacy`}><FormattedMessage {...termsDefinitions.privacy_policy} /></a>,
        cancellation_policy: <a href={`${urlPrefix}/legal/terms/cancellation-policy`}><FormattedMessage {...termsDefinitions.cancellation_policy} /></a>,
        fees_overview: <a href={`${urlPrefix}/legal/terms/fees-overview`}><FormattedMessage {...termsDefinitions.fees_overview} /></a>,
        community_guidelines: <a href={`${urlPrefix}/legal/terms/community-guidelines`}><FormattedMessage {...termsDefinitions.community_guidelines} /></a>
      }}
    />
  ),
  banking: (
    <FormattedMessage
      id="shared_components.disclaimer.banking_terms"
      defaultMessage="you agree to {terms_link}, and {stripe_link} which includes the {connected_link}."
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`}><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        stripe_link: <a href="https://stripe.com/legal"><FormattedMessage {...termsDefinitions.stripe_service_agreement} /></a>,
        connected_link: <a href="https://stripe.com/connect-account/legal"><FormattedMessage {...termsDefinitions.stripe_connected} /></a>
      }}
    />
  ),
  creditCard: (
    <FormattedMessage
      id="shared_components.disclaimer.credit_card"
      defaultMessage="you agree to {terms_link},  and {stripe_link} which includes the {connected_link}."
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`}><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        stripe_link: <a href="https://stripe.com/legal"><FormattedMessage {...termsDefinitions.stripe_service_agreement} /></a>,
        connected_link: <a href="https://stripe.com/connect-account/legal"><FormattedMessage {...termsDefinitions.stripe_connected} /></a>
      }}
    />
  ),
  directDeposit: (
    <FormattedMessage
      id="shared_components.disclaimer.direct_deposit"
      defaultMessage="you agree to {terms_link},  and {stripe_link} which includes the {connected_link}."
      values={{
        terms_link: <a className="underline" href={`${urlPrefix}/legal/terms`}><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        stripe_link: <a className="underline" href="https://stripe.com/legal"><FormattedMessage {...termsDefinitions.stripe_service_agreement} /></a>,
        connected_link: <a className="underline" href="https://stripe.com/connect-account/legal"><FormattedMessage {...termsDefinitions.stripe_connected} /></a>
      }}
    />
  ),
  verify: (
    <FormattedMessage
      id="shared_components.disclaimer.verify"
      defaultMessage="you agree to {terms_link}."
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`}><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>
      }}
    />
  ),
  requestBkgInsta: (
    <FormattedMessage
      id="shared_components.disclaimer.instant_book_req"
      defaultMessage="you also agree to {terms_link}, which includes the {community_guidelines} and {cancellation_policy}."
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        community_guidelines: <a href={`${urlPrefix}/legal/terms/community-guidelines`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.community_guidelines} /></a>,
        cancellation_policy: <a href={`${urlPrefix}/legal/terms/cancellation-policy`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.cancellation_policy} /></a>
      }}
    />
  ),
  requestBkg: (
    <FormattedMessage
      id="shared_components.disclaimer.request_booking"
      defaultMessage="you agree to {terms_link}, which includes the {community_guidelines}, and {cancellation_policy}."
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`}><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        community_guidelines: <a href={`${urlPrefix}/legal/terms/community-guidelines`}><FormattedMessage {...termsDefinitions.community_guidelines} /></a>,
        cancellation_policy: <a href={`${urlPrefix}/legal/terms/cancellation-policy`}><FormattedMessage {...termsDefinitions.cancellation_policy} /></a>
      }}
    />
  ),
  requestBkgNewTab: (
    <FormattedMessage
      id="shared_components.disclaimer.request_booking_new_tab"
      defaultMessage="you also agree to {terms_link}, which includes the {community_guidelines} and {cancellation_policy}."
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        community_guidelines: <a href={`${urlPrefix}/legal/terms/community-guidelines`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.community_guidelines} /></a>,
        cancellation_policy: <a href={`${urlPrefix}/legal/terms/cancellation-policy`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.cancellation_policy} /></a>
      }}
    />
  ),
  acceptBkg: (
    <FormattedMessage
      id="shared_components.disclaimer.accept_booking"
      defaultMessage="you agree to {terms_link}, which includes our {community_guidelines}, {social_event_policy}, and {cancellation_policy}"
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`}><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        community_guidelines: <a href={`${urlPrefix}/legal/terms/community-guidelines`}><FormattedMessage {...termsDefinitions.community_guidelines} /></a>,
        social_event_policy: <a href={`${urlPrefix}/legal/terms/social-event-policy`}><FormattedMessage {...termsDefinitions.social_event_policy} /></a>,
        cancellation_policy: <a href={`${urlPrefix}/legal/terms/cancellation-policy`}><FormattedMessage {...termsDefinitions.cancellation_policy} /></a>
      }}
    />
  ),
  acceptBkgNewTab: (
    <FormattedMessage
      id="shared_components.disclaimer.accept_booking_new_tab"
      defaultMessage="you agree to {terms_link}, which includes the {community_guidelines}, and {cancellation_policy}."
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        community_guidelines: <a href={`${urlPrefix}/legal/terms/community-guidelines`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.community_guidelines} /></a>,
        cancellation_policy: <a href={`${urlPrefix}/legal/terms/cancellation-policy`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.cancellation_policy} /></a>
      }}
    />
  ),
  cancelBkg: (<FormattedMessage
    id="shared_components.disclaimer.cancel_booking"
    defaultMessage="you agree to {terms_link}, which includes the {cancellation_policy}."
    values={{
      terms_link: <a href={`${urlPrefix}/legal/terms`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
      cancellation_policy: <a href={`${urlPrefix}/legal/terms/cancellation-policy`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.cancellation_policy} /></a>
    }}
  />),
  cancelBkgInsta: (
    <FormattedMessage
      id="shared_components.disclaimer.cancel_instant_book"
      defaultMessage="you agree to {instant_book} as well as {terms_link}, which includes the {cancellation_policy}."
      values={{
        instant_book: <a href={`//support.peerspace.com/hc/${zenDeskLocale}/articles/360017989772`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.instant_book} /></a>,
        terms_link: <a href={`${urlPrefix}/legal/terms`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        cancellation_policy: <a href={`${urlPrefix}/legal/terms/cancellation-policy`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.cancellation_policy} /></a>
      }}
    />
  ),
  cancelBkgCredits: (
    <FormattedMessage
      id="shared_components.disclaimer.cancel_booking_credits"
      defaultMessage="Peerspace Credits will be automatically issued to your Peerspace account. Once cancelled, no further changes or reversals may be made. You agree to {terms_link}, which includes the {cancellation_policy}."
      values={{
        terms_link: <a href={`${urlPrefix}/legal/terms`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.peerspace_service_agreement} /></a>,
        cancellation_policy: <a href={`${urlPrefix}/legal/terms/cancellation-policy`} target="_blank" rel="noopener noreferrer"><FormattedMessage {...termsDefinitions.cancellation_policy} /></a>
      }}
    />
  )
};

export const TosCTAdisclaimer = (props) => {
  const { buttonLabel, location } = props;
  const clickDesc = <span><FormattedMessage id="shared_components.disclaimer.click_prefix" defaultMessage="By clicking"/> <strong>{ buttonLabel },</strong> </span>;
  const termsGroup = termsLinks[location];
  return (
    <p className={ `cta-disclaimer ${ location }` }>
      { clickDesc }
      { termsGroup }
    </p>
  );
};

TosCTAdisclaimer.propTypes = {
  buttonLabel: PropTypes.string.isRequired,
  location: PropTypes.oneOf(['signup', 'transactable', 'banking', 'verify', 'requestBkg', 'acceptBkg', 'cancelBkg', 'directDeposit', 'requestBkgInsta', 'cancelBkgInsta', 'cancelBkgCredits']).isRequired
};
